import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Menu} from '@mantine/core';
import React from 'react';

import ArchiveModal from 'archive/components/modals/ArchiveModal';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {getOrCreateDraftState, setDraftState, ChatDraftStatus} from 'llm/chat-library/ChatDraftState';
import {useAISidepanelGlobalClientState} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {downloadSkillBundle, SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import EntityStore from 'stores/EntityStore';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

/** How the user wants to bring a skill into chat: run it, or edit its definition. */
export enum SkillChatMode {
  TRY = 'TRY',
  EDIT = 'EDIT',
}

/** Build the prefilled draft text for bringing a skill into chat. */
export function buildSkillDraftText(currentText: string, skill: SkillSummary, mode: SkillChatMode): string {
  const skillMention = `/${skill.name}`;
  if (currentText.trim().length === 0) {
    return mode === SkillChatMode.EDIT
      ? `Edit the ${skillMention} skill to `
      : `Use the ${skillMention} skill to `;
  }
  const prefix = currentText.endsWith(' ') ? '' : ' ';
  return `${currentText}${prefix}${skillMention} `;
}

interface SkillActionsMenuProps {
  skill: SkillSummary;
  /**
   * Component name used for test props, so that consumers (list vs. detail) keep
   * distinct, stable test ids while sharing a single menu implementation.
   */
  testComponentName: string;
  onArchive: (skillId: string, reason: string) => void;
}

/**
 * The overflow ("more options") menu shown for a skill. Shared between the skills
 * list view and the skill detail view so both surface the same actions.
 */
export default function SkillActionsMenu({
  skill,
  testComponentName,
  onArchive,
}: SkillActionsMenuProps): JSX.Element {
  const [panelState, setPanelState] = useAISidepanelGlobalClientState();

  function writeSkillToDraft(skill: SkillSummary, mode: SkillChatMode): void {
    const chatSessionId = panelState.activeChat.id;
    const draftState = getOrCreateDraftState(chatSessionId);
    const currentText = draftState.message.text;
    setDraftState(chatSessionId, {
      ...draftState,
      status: ChatDraftStatus.DRAFT,
      message: {
        ...draftState.message,
        text: buildSkillDraftText(currentText, skill, mode),
      },
    });
    setPanelState({...panelState, activeSettingsTab: null});
  }

  function onTryWithChat(skill: SkillSummary): void {
    writeSkillToDraft(skill, SkillChatMode.TRY);
  }

  function onEditWithChat(skill: SkillSummary): void {
    writeSkillToDraft(skill, SkillChatMode.EDIT);
  }

  const isTenantAdmin = EntityStore.currentUser()?.isTenantAdmin ?? false;
  // Extension-installed skills cannot be archived; otherwise admins/owners and tenant admins can.
  const canArchive = skill.extensionDefinitionId == null && (skill.canManageAccess || isTenantAdmin);
  // Only surface "Edit with chat" when the user actually has write access to the skill.
  const canEditWithChat = skill.canEdit;

  function handleArchiveClick(): void {
    legacyRunModal(ArchiveModal, {
      items: [{id: skill.id, name: skill.name}],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        for (const id of itemIds) {
          onArchive(id, purpose);
        }
      },
    });
  }

  return (
    <Menu position="bottom-end" withinPortal={true}>
      <Menu.Target>
        <ActionIcon
          size="sm"
          variant="subtle"
          aria-label="More options"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
          {...getTestProps({component: testComponentName, element: 'moreOptionsButton'})}
        >
          <BenchlingIcon icon="ellipsis-vertical" />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            onTryWithChat(skill);
          }}
          {...getTestProps({component: testComponentName, element: 'tryWithChatItem'})}
        >
          Try with chat
        </Menu.Item>
        {canEditWithChat && (
          <Menu.Item
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onEditWithChat(skill);
            }}
            {...getTestProps({component: testComponentName, element: 'editWithChatItem'})}
          >
            Edit with chat
          </Menu.Item>
        )}
        <Menu.Item
          onClick={() => {
            downloadSkillBundle(skill.id);
          }}
          {...getTestProps({component: testComponentName, element: 'downloadSkillItem'})}
        >
          Download skill
        </Menu.Item>
        {canArchive && (
          <Menu.Item
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleArchiveClick();
            }}
            {...getTestProps({component: testComponentName, element: 'archiveSkillItem'})}
          >
            Archive
          </Menu.Item>
        )}
        <Menu.Item
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            exportAuditLogs([skill.id]);
          }}
          {...getTestProps({component: testComponentName, element: 'exportAuditLogItem'})}
        >
          {EXPORT_AUDIT_LOG_DISPLAY_STRING}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
